import { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import LocalePicker from "components/Elements/LocalePicker";
import { localeOptions } from "helpers/localeOptions";

const DEFAULTS = {
	sourceTerm: "",
	targetTerm: "",
	context: "",
};

const findLocaleByValue = (value) =>
	localeOptions.find((opt) => opt.value === value) ?? null;

const AddEntryDialog = ({
	open,
	closeHandler,
	onSubmit,
	defaultLocale,
	editEntry,
}) => {
	const isEdit = !!editEntry;
	const [values, setValues] = useState(DEFAULTS);
	const [locale, setLocale] = useState(null);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!open) return;
		if (editEntry) {
			setValues({
				sourceTerm: editEntry.sourceTerm ?? "",
				targetTerm: editEntry.targetTerm ?? "",
				context: editEntry.context ?? "",
			});
			setLocale(findLocaleByValue(editEntry.targetLanguage) ?? null);
		} else {
			setValues(DEFAULTS);
			setLocale(
				findLocaleByValue(defaultLocale) ??
					localeOptions.find((opt) => opt.language === "es") ??
					localeOptions[0] ??
					null
			);
		}
		setErrors({});
	}, [open, defaultLocale, editEntry]);

	const update = (field, value) =>
		setValues((prev) => ({ ...prev, [field]: value }));

	const validate = () => {
		const next = {};
		if (!values.sourceTerm.trim()) next.sourceTerm = "Required";
		if (!values.targetTerm.trim()) next.targetTerm = "Required";
		if (!locale) next.locale = "Required";
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;
		onSubmit({
			sourceTerm: values.sourceTerm.trim(),
			targetTerm: values.targetTerm.trim(),
			targetLanguage: locale.value,
			context: values.context.trim() || null,
		});
	};

	return (
		<Dialog open={open} onClose={closeHandler} fullWidth maxWidth="sm">
			<DialogTitle>{isEdit ? "Edit Glossary Entry" : "Add Glossary Entry"}</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					fullWidth
					label="Source term"
					required
					value={values.sourceTerm}
					onChange={(e) => update("sourceTerm", e.target.value)}
					error={!!errors.sourceTerm}
					helperText={errors.sourceTerm || "The English term as it appears in the model"}
					sx={{ mt: 1, mb: 2 }}
				/>
				<LocalePicker
					value={locale}
					onChange={setLocale}
					label="Target locale"
					required
					helperText={
						errors.locale || "Type a language or country to filter"
					}
					sx={{ mb: 2 }}
				/>
				<TextField
					fullWidth
					label="Target term"
					required
					value={values.targetTerm}
					onChange={(e) => update("targetTerm", e.target.value)}
					error={!!errors.targetTerm}
					helperText={
						errors.targetTerm ||
						"How it should appear after translation (or keep unchanged, e.g. 'LOTO')"
					}
					sx={{ mb: 2 }}
				/>
				<TextField
					fullWidth
					label="Context"
					value={values.context}
					onChange={(e) => update("context", e.target.value)}
					placeholder="Optional"
					helperText="Helps disambiguate when the same source has different translations in different domains"
					multiline
					minRows={2}
				/>
				<Typography variant="caption" sx={{ display: "block", mt: 2, color: "#666" }}>
					Tip: leave Target term identical to Source if it should pass through
					unchanged (e.g. acronyms like "LOTO", "PPE").
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={closeHandler}>Cancel</Button>
				<Button variant="contained" onClick={handleSubmit}>
					{isEdit ? "Save changes" : "Add entry"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddEntryDialog;
